import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { usePlatformAdmin } from '../../hooks/usePlatformAdmin';
import { useIsMobile } from '../../hooks/use-mobile';
import { Button } from '../ui/button';
import { Settings, User, LogOut, ChevronDown, Home } from 'lucide-react';
import { PlatformTenantSelector } from '../admin/PlatformTenantSelector';
import { TenantLogo } from './TenantLogo';
import { NotificationCenter } from '../notifications/NotificationCenter';
import { ModuleSwitcher } from './ModuleSwitcher';

interface AppHeaderProps {
  showAdminButton?: boolean;
  showBackToPortal?: boolean;
  showSettings?: boolean;
  onSettingsClick?: () => void;
}

export const AppHeader: React.FC<AppHeaderProps> = ({ 
  showAdminButton = false,
  showBackToPortal = false,
  showSettings = false,
  onSettingsClick,
}) => {
  const { user, signOut } = useAuth();
  const { hasAdminAccess, isSuperAdmin, loading: adminLoading } = usePlatformAdmin();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  
  const getRoleLabel = () => {
    if (isSuperAdmin) return 'Super Admin';
    if (hasAdminAccess) return 'Platform Admin';
    return 'User';
  };

  const handleSignOut = async () => {
    try {
      // Clear super admin tenant selection on logout for security
      localStorage.removeItem('super_admin_selected_tenant_id');
      await signOut();
      navigate('/login');
    } catch (error) {
      console.error('Sign out error:', error);
    }
  };

  return (
    <header className="bg-card border-b border-border sticky top-0 z-30 backdrop-blur-sm bg-card/95">
      <div className="w-full px-2 sm:px-4 lg:px-8 xl:px-12 2xl:px-16">
        <div className={`flex items-center justify-between ${isMobile ? 'h-14' : 'h-16'}`}>
          {/* Logo */}
          <div className="flex items-center gap-4 flex-shrink-0">
            <button 
              onClick={() => navigate('/portal')}
              className="flex items-center hover:opacity-80 transition-opacity"
            >
              <TenantLogo className="h-6 w-auto min-w-[24px] object-contain" />
            </button>
            {showBackToPortal && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate('/portal')}
                className="flex items-center gap-2 text-muted-foreground hover:text-foreground"
              >
                <Home className="h-4 w-4" />
                <span className="hidden sm:inline">Clients Portal</span>
              </Button>
            )}
          </div>

          {/* Right side - Platform Tenant Selector, Admin Settings & User Menu */}
          <div className="flex items-center gap-3 flex-shrink-0">
            {/* Platform Tenant Selector for Super Admins — always in DOM to prevent layout shift */}
            <div className={isSuperAdmin ? 'hidden md:flex items-center' : 'hidden'}>
              <PlatformTenantSelector compact />
            </div>
            
            {/* Admin Dashboard Button */}
            {showAdminButton && hasAdminAccess && !adminLoading && (
              <Button 
                variant="ghost" 
                size="sm"
                onClick={() => navigate('/admin')}
                className="flex items-center gap-2"
              >
                <Settings className="h-4 w-4" />
                <span className="hidden sm:inline">Admin</span>
              </Button>
            )}

            {/* Module Switcher - unified on both desktop and mobile */}
            <ModuleSwitcher />

            {/* Notifications */}
            <NotificationCenter 
              onSettingsClick={() => navigate('/portal/settings')}
            />

            {/* Settings Icon Button */}
            {showSettings && (
              <button
                onClick={onSettingsClick}
                className="p-2 rounded hover:bg-muted transition-colors"
                title="Settings"
              >
                <Settings className="h-5 w-5 text-muted-foreground hover:text-foreground" />
              </button>
            )}

            {/* User Menu */}
            <div className="relative">
              <button
                onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
                className="flex items-center gap-2 px-3 py-2 rounded hover:bg-muted transition-colors"
              >
                <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                  <User className="h-4 w-4 text-primary" />
                </div>
                <ChevronDown className="h-4 w-4 text-muted-foreground" />
              </button>

              {/* Dropdown Menu */}
              {isUserMenuOpen && (
                <>
                  <div 
                    className="fixed inset-0 z-10" 
                    onClick={() => setIsUserMenuOpen(false)}
                  />
                  <div className="absolute right-0 mt-2 w-64 bg-card border border-border rounded shadow-md z-20 overflow-hidden animate-scale-in">
                    {/* User Info */}
                    <div className="px-4 py-3 border-b border-border bg-muted/50">
                      <p className="text-sm font-medium text-foreground truncate">
                        {user?.email}
                      </p>
                      <p className="text-xs text-muted-foreground mt-1">
                        {getRoleLabel()}
                      </p>
                    </div>

                    {/* Menu Items */}
                    <div className="py-1">
                      <button
                        onClick={() => {
                          setIsUserMenuOpen(false);
                          navigate('/profile-settings');
                        }}
                        className="w-full px-4 py-2.5 text-left text-sm hover:bg-muted flex items-center gap-3 transition-colors"
                      >
                        <User className="h-4 w-4 text-muted-foreground" />
                        <span className="text-foreground">Profile Settings</span>
                      </button>
                      <button
                        onClick={() => {
                          setIsUserMenuOpen(false);
                          handleSignOut();
                        }}
                        className="w-full px-4 py-2.5 text-left text-sm hover:bg-destructive/10 flex items-center gap-3 transition-colors text-destructive"
                      >
                        <LogOut className="h-4 w-4" />
                        <span className="font-medium">Sign Out</span>
                      </button>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </header>
  );
};
